import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import Plugin from "@/components/Plugin";
import AiPage from "@/components/AiPage";
import AdminPage from "@/components/AdminPage";
import PaymentPage from "@/components/PaymentPage";
import ActivatePage from "@/components/ActivatePage";
import CheckoutPage from "@/components/CheckoutPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API });
export const LICENCE_STORAGE = "kk_licence_key";
api.interceptors.request.use((c) => {
  const key = localStorage.getItem(LICENCE_STORAGE);
  if (key && /^\/(matte|licence)/.test(c.url || "")) c.headers["X-Licence-Key"] = key;
  return c;
});

function App() {
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/admin") return <AdminPage />;
  if (path.startsWith("/payment/")) return <PaymentPage />;
  if (path === "/activate") return <ActivatePage />;
  if (path === "/checkout") return <CheckoutPage />;
  return <Site ai={path === "/ai"} />;
}

const AI_NAV = [
  ["/", "← Finishing tools", "text-[#9ba3b4]", "nav-finishing"],
  ["#gen-alpha", "Gen Alpha", "text-[#e69d27]", "nav-gen-alpha"],
  ["#recast", "Recast", "text-[#4ff2c2]", "nav-recast"],
  ["#fal-key", "fal.ai key", "text-[#9ba3b4]", "nav-fal-key"],
  ["#pricing", "Pricing", "text-white", "nav-pricing"],
  ["#support", "Support", "text-[#9ba3b4]", "nav-support"],
];

function Site({ ai }) {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    const h = window.location.hash;
    if (!h) return;
    const t = setTimeout(() => document.querySelector(h)?.scrollIntoView(), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    api.get("/health")
      .then((r) => setHealth(r.data))
      .catch(() => setHealth({ ok: false }));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#0b0c0e]">
      <header
        className="relative hair-border border-x-0 border-t-0 bg-[#121418]/80 backdrop-blur-md"
        data-testid="app-header"
      >
        <div className="max-w-[1480px] mx-auto flex items-center gap-6 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rec-dot" aria-hidden />
            <a href="/" className="font-display font-semibold text-xl uppercase leading-none" style={{ letterSpacing: "0.26em" }} data-testid="brand-home-link">KolorKlinic{ai && <span className="text-[#4ff2c2]"> AI</span>}</a>
            <span className="font-display font-semibold text-xs text-[#9ba3b4] uppercase tracking-[0.3em]">
              Native OFX for Resolve
            </span>
          </div>
          {ai ? (
            <nav className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest" data-testid="ai-nav">
              {AI_NAV.map(([href, label, cls, tid]) => (
                <a key={tid} href={href} className={`px-3 py-1.5 border border-transparent hover:border-[#2a2f3d] ${cls} transition-colors`} data-testid={tid}>{label}</a>
              ))}
            </nav>
          ) : (
          <nav className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest overflow-x-auto max-w-full whitespace-nowrap" data-testid="product-nav">
            <a href="#texture" className="px-3 py-1.5 border border-transparent hover:border-[#ff6a3d] text-[#ff6a3d] transition-colors" data-testid="nav-texture">Texture</a>
            <a href="#reactor" className="px-3 py-1.5 border border-transparent hover:border-[#c48bff] text-[#c48bff] transition-colors" data-testid="nav-reactor">Reactor <span className="text-[10px] text-[#c48bff]">new</span></a>
            <a href="#skin" className="px-3 py-1.5 border border-transparent hover:border-[#ff7a9c] text-[#ff7a9c] transition-colors" data-testid="nav-skin">Skin</a>
            <a href="#denoise" className="px-3 py-1.5 border border-transparent hover:border-[#00e5ff] text-[#00e5ff] transition-colors" data-testid="nav-denoise">Denoise</a>
            <a href="#film-compression" className="px-3 py-1.5 border border-transparent hover:border-[#f2c46d] text-[#f2c46d] transition-colors" data-testid="nav-film-compression">Film Compression <span className="text-[10px] text-[#f2c46d]">free</span></a>
            <a href="/ai" className="px-3 py-1.5 border border-[#4ff2c2]/50 hover:border-[#4ff2c2] text-[#4ff2c2] transition-colors" data-testid="nav-ai">AI <span className="text-[10px] text-[#e69d27]">new</span></a>
            <a href="#pricing" className="px-3 py-1.5 border border-transparent hover:border-white text-white transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#support" className="px-3 py-1.5 border border-transparent hover:border-white text-[#9ba3b4] hover:text-white transition-colors" data-testid="nav-support">Support</a>
          </nav>
          )}
          <div className="hidden md:flex items-center gap-2 chip ml-auto" data-testid="health-chip">
            <span className={`status-dot ${health?.ok ? "ok" : health === null ? "idle" : "err"}`} />
            <span>{health?.ok ? "backend online" : health === null ? "pinging…" : "offline"}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1480px] w-full mx-auto px-6 py-8">
        {ai ? <AiPage /> : <Plugin health={health} />}
      </main>

      <footer
        className="hair-border border-x-0 border-b-0 px-6 py-4 text-xs text-[#5c6479] font-mono uppercase tracking-widest flex gap-6 flex-wrap"
        data-testid="app-footer"
      >
        <span>{ai ? "KOLORKLINIC / AI tools" : "KOLORKLINIC / finishing tools"}</span>
        <a href={ai ? "/" : "/ai"} className="text-[#9ba3b4] hover:text-white transition-colors" data-testid="footer-switch-link">{ai ? "Finishing tools" : "KolorKlinic AI"}</a>
        <span>OFX API v1.4</span>
        <span className="text-[#9ba3b4]" data-testid="footer-tagline">made by colorists, for colorists</span>
        <a href="#support" className="text-[#9ba3b4] hover:text-white transition-colors" data-testid="footer-support-link">Support · contact</a>
        <span className="ml-auto">unsigned · ad-hoc</span>
      </footer>
    </div>
  );
}

export default App;
